import { API_BASE } from './api.ts';
import type { LngLat } from './types.ts';

/**
 * Anywhere in the world, as a destination.
 *
 * The course directory answers "which course"; this answers "the McDonald's on
 * the way home". Both feed the same search box, because a golfer typing into it
 * does not think in terms of which dataset holds the answer.
 *
 * Served by our own Worker rather than a geocoder directly — see
 * worker/src/index.ts. The app never learns which provider is behind it.
 */

export interface Place {
  name: string;
  /** Street, suburb, state, country — whatever the provider had. */
  where: string;
  pos: LngLat;
}

/**
 * Debounced, cancelling lookup.
 *
 * Every keystroke firing its own request is how a search box ends up showing
 * the results for "mcdon" after the ones for "mcdonalds" — the responses come
 * back out of order. An AbortController on the previous request makes the last
 * keystroke the only one that can win.
 */
export function createPlaceSearch(onResults: (places: Place[]) => void, delayMs = 250) {
  let timer: number | undefined;
  let inFlight: AbortController | null = null;

  const run = async (query: string, near: LngLat | null) => {
    inFlight?.abort();
    const ctl = new AbortController();
    inFlight = ctl;
    const params = new URLSearchParams({ q: query });
    if (near) {
      params.set('lat', String(near[1]));
      params.set('lng', String(near[0]));
    }
    try {
      // API_BASE, not a bare path: inside the app a relative URL resolves
      // against capacitor://localhost and never reaches the server.
      const r = await fetch(`${API_BASE}/api/places?${params}`, { signal: ctl.signal });
      if (!r.ok) return onResults([]);
      const rows = (await r.json()) as { name: string; where: string; lat: number; lng: number }[];
      if (ctl.signal.aborted) return;
      onResults(rows.map((p) => ({ name: p.name, where: p.where, pos: [p.lng, p.lat] as LngLat })));
    } catch {
      // Aborted, offline, or the provider is down. The course list still works,
      // which is the part that has to keep working on a course with no signal.
      if (!ctl.signal.aborted) onResults([]);
    }
  };

  return {
    search(query: string, near: LngLat | null) {
      window.clearTimeout(timer);
      const q = query.trim();
      if (q.length < 2) {
        inFlight?.abort();
        onResults([]);
        return;
      }
      timer = window.setTimeout(() => void run(q, near), delayMs);
    },
    stop() {
      window.clearTimeout(timer);
      inFlight?.abort();
    },
  };
}
